import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../lib/utils.ts"
import { Tabs as TabsPrimitive } from "radix-ui"

/*
 * shadcn/ui Tabs, restyled (components.md, Tabs and navigation): an underline row, the
 * selected tab marked twice (a sliding 2 px indicator in the control line colour, one hue, and weight 500 in fg). 6 tabs or fewer,
 * one row. The `default` variant is the shadcn well, kept for compact panels; pages use
 * `line`. A view switch of the same content is a Segmented, not tabs.
 */
function Tabs({
  className,
  orientation = "horizontal",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      orientation={orientation}
      className={cn(
        "group/tabs flex gap-2 data-[orientation=horizontal]:flex-col",
        className
      )}
      {...props}
    />
  )
}

const tabsListVariants = cva(
  "group/tabs-list inline-flex items-center text-fg-subtle group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col",
  {
    variants: {
      variant: {
        default:
          "w-fit justify-center rounded-control border border-border bg-sunken p-0.5 group-data-[orientation=horizontal]/tabs:h-control",
        line: "w-full justify-start gap-4 border-b border-border-subtle group-data-[orientation=horizontal]/tabs:h-control-lg",
      },
    },
    defaultVariants: {
      variant: "line",
    },
  }
)

function TabsList({
  className,
  variant = "line",
  children,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  const listRef = React.useRef<HTMLDivElement>(null)
  const barRef = React.useRef<HTMLSpanElement>(null)

  // The line variant's indicator: one 2 px bar in the control line colour (one hue, never multicolour) that slides under the active tab. It
  // is a 1 px wide element placed by transform (translateX to the tab, scaleX to its width), so
  // only transform animates (motion/layout-property). The first placement snaps; later ones
  // glide over --duration-base, and reduced motion zeroes that token.
  React.useLayoutEffect(() => {
    if (variant !== "line") return
    const list = listRef.current
    const bar = barRef.current
    if (!list || !bar) return
    const place = () => {
      const active = list.querySelector<HTMLElement>('[role="tab"][data-state="active"]')
      if (!active || list.closest('[data-orientation="vertical"]')) {
        bar.style.opacity = "0"
        return
      }
      bar.style.opacity = "1"
      bar.style.transform = `translateX(${active.offsetLeft}px) scaleX(${active.offsetWidth})`
    }
    place()
    const settle = requestFrame(() => bar.setAttribute("data-ready", ""))
    const mo = new MutationObserver(place)
    mo.observe(list, { subtree: true, childList: true, attributes: true, attributeFilter: ["data-state"] })
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place)
    ro?.observe(list)
    for (const tab of Array.from(list.querySelectorAll('[role="tab"]'))) ro?.observe(tab)
    return () => {
      settle()
      mo.disconnect()
      ro?.disconnect()
    }
  }, [variant])

  return (
    <TabsPrimitive.List
      ref={listRef}
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(tabsListVariants({ variant }), variant === "line" && "relative", className)}
      {...props}
    >
      {children}
      {variant === "line" && (
        <span
          ref={barRef}
          aria-hidden
          data-slot="tabs-indicator"
          className="pointer-events-none absolute -bottom-px left-0 h-0.5 w-px origin-left bg-(--control-line) opacity-0 data-ready:transition-transform data-ready:duration-(--duration-base) data-ready:ease-standard"
        />
      )}
    </TabsPrimitive.List>
  )
}

/** Run once after the next paint without a frame loop of our own (motion/own-timer). */
function requestFrame(fn: () => void): () => void {
  const t = setTimeout(fn, 0)
  return () => clearTimeout(t)
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "relative inline-flex h-full items-center justify-center gap-1.5 whitespace-nowrap rounded-control text-ui text-fg-subtle transition-colors duration-(--duration-fast) ease-standard hover:text-fg focus-visible:-outline-offset-2 disabled:pointer-events-none disabled:text-fg-faint data-[state=active]:font-medium data-[state=active]:text-fg group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "group-data-[variant=default]/tabs-list:flex-1 group-data-[variant=default]/tabs-list:rounded-control group-data-[variant=default]/tabs-list:px-2 group-data-[variant=default]/tabs-list:data-[state=active]:bg-raised group-data-[variant=default]/tabs-list:data-[state=active]:shadow-elevation-flat",
        "group-data-[variant=line]/tabs-list:-mb-px",
        className
      )}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1", className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants }
